import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  PREBOARDING_FORM_VARIABLES,
  type PreboardingFormVariableGroup,
} from '@hygge/shared';
import { Braces } from 'lucide-react';

const GROUP_ORDER: PreboardingFormVariableGroup[] = ['Employee', 'Company'];

interface AddVariableMenuProps {
  /** Receives the bare token (e.g. "employeeFirstName"); caller wraps in {{ }}. */
  onInsert: (token: string) => void;
  /** Render a compact icon-only trigger (used inside the editor toolbar). */
  compact?: boolean;
}

export function AddVariableMenu({ onInsert, compact }: AddVariableMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          title="Add variable"
          className={compact ? 'h-8 gap-1 px-2 text-xs' : 'h-8 gap-1.5 text-xs'}
        >
          <Braces className="h-3.5 w-3.5" />
          {compact ? 'Variable' : 'Add variable'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {GROUP_ORDER.map((group, groupIndex) => {
          const items = PREBOARDING_FORM_VARIABLES.filter((v) => v.group === group);
          if (items.length === 0) return null;
          return (
            <div key={group}>
              {groupIndex > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {group}
              </DropdownMenuLabel>
              {items.map((variable) => (
                <DropdownMenuItem
                  key={variable.token}
                  onSelect={() => onInsert(variable.token)}
                  className="flex items-center justify-between gap-3"
                >
                  <span>{variable.label}</span>
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {`{{${variable.token}}}`}
                  </span>
                </DropdownMenuItem>
              ))}
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
